import { ref } from 'vue';
import { useActionStream } from '@/composables/useActionStream';
import { ActionV2 } from '@/api/action';

export function useAppManagement(deviceId) {
    const { sendAction, awaitResult } = useActionStream(deviceId);

    const apps = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const actionLoading = ref({});
    const appActions = ref([]);
    const appActionsLoading = ref(false);

    const fetchApps = async () => {
        loading.value = true;
        error.value = null;
        try {
            const { ok, action_id } = await sendAction({
                action_class: 'app',
                action: 'list',
                action_type: 'Read',
                payload: {},
            });
            if (!ok) throw new Error('Failed to send app list request');
            const result = await awaitResult(action_id);
            apps.value = result?.apps ?? [];
        } catch (e) {
            error.value = e.message || 'Failed to fetch apps';
        } finally {
            loading.value = false;
        }
    };

    const fetchAppActions = async () => {
        appActionsLoading.value = true;
        try {
            const response = await ActionV2.get();
            if (response.ok) {
                const allActions = (await response.json()).actions || {};
                const appClass = allActions.app || {};
                const typeOrder = ['Execute', 'Read', 'Create', 'Update', 'Delete'];
                const result = [];
                for (const [action, details] of Object.entries(appClass)) {
                    if (action === 'name' || action === 'list') continue;
                    for (const actionType of typeOrder) {
                        if (details[actionType]) {
                            result.push({
                                action,
                                actionType,
                                name: details.name || action,
                                description: details[actionType].description || '',
                            });
                            break;
                        }
                    }
                }
                appActions.value = result;
            }
        } catch {
            // Leave appActions empty; component shows nothing rather than crashing
        } finally {
            appActionsLoading.value = false;
        }
    };

    // Errors propagate to callers — each call site handles its own error display.
    const sendAppAction = async (appId, action, actionType = 'Execute') => {
        const key = `${appId}:${action}`;
        actionLoading.value = { ...actionLoading.value, [key]: true };
        try {
            const { ok, action_id } = await sendAction({
                action_class: 'app',
                action,
                action_type: actionType,
                payload: { app_id: appId },
            });
            if (!ok) throw new Error('Failed to send action');
            return await awaitResult(action_id);
        } finally {
            const updated = { ...actionLoading.value };
            delete updated[key];
            actionLoading.value = updated;
        }
    };

    return { apps, loading, error, actionLoading, appActions, appActionsLoading, fetchApps, fetchAppActions, sendAppAction, sendAction, awaitResult };
}
